import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { TrendingUp, TrendingDown, Eye, EyeOff } from 'lucide-react';
import { TokenBalance } from '../../types/wallet';

// Mock asset data
const mockAssets: TokenBalance[] = [
  {
    address: '0x...eth',
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    balance: '5.2847',
    price: 2845.67,
    value: 15038.45,
    logoURI: '',
  },
  {
    address: '0x...ndh',
    symbol: 'NDH',
    name: 'NDH Token',
    decimals: 18,
    balance: '10000',
    price: 0.89,
    value: 8900,
    logoURI: '',
  },
  {
    address: '0x...usdc',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    balance: '1234.56',
    price: 1.00,
    value: 1234.56,
    logoURI: '',
  },
  {
    address: '0x...uni',
    symbol: 'UNI',
    name: 'Uniswap',
    decimals: 18,
    balance: '25.5',
    price: 12.45,
    value: 317.48,
    logoURI: '',
  },
];

export function AssetList() {
  const [hideSmallBalances, setHideSmallBalances] = React.useState(false);

  const filteredAssets = hideSmallBalances 
    ? mockAssets.filter(asset => asset.value > 1)
    : mockAssets;

  const totalValue = mockAssets.reduce((sum, asset) => sum + asset.value, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Assets</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHideSmallBalances(!hideSmallBalances)}
        >
          {hideSmallBalances ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredAssets.map((asset) => {
            const change24h = Math.random() * 20 - 10; // Mock 24h change
            const isPositive = change24h >= 0;
            
            return (
              <div key={asset.address} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    {asset.symbol.slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-medium">{asset.symbol}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {asset.name}
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-medium">
                    ${asset.value.toLocaleString()}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {parseFloat(asset.balance).toFixed(4)} {asset.symbol}
                  </div>
                  <div className="flex items-center gap-1 justify-end text-xs">
                    {isPositive ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-red-500" />
                    )}
                    <span className={isPositive ? 'text-green-500' : 'text-red-500'}>
                      {isPositive ? '+' : ''}{change24h.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Total Portfolio Value</span>
            <span className="font-medium">${totalValue.toLocaleString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}